import { useEffect, useState } from "react";
import api, { errMsg } from "../api";
import { Card, Select, BtnDanger, BtnSecondary, Modal, Input } from "../components/UI";
import { RollLink } from "../components/RollLink";
import { useAuth } from "../contexts/AuthContext";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, LineChart, Line, Legend } from "recharts";
import {
  AlertTriangle, Factory, Scissors, Printer, Shirt, Flame, Boxes, Truck,
  CheckCircle2, TrendingUp, TrendingDown, Minus, Clock, RotateCcw, Clock3, Percent, Package, XCircle
} from "lucide-react";
import { toast } from "sonner";

const PERIODS = [
  { v: "today", label: "Today" },
  { v: "yesterday", label: "Yesterday" },
  { v: "this_week", label: "This Week" },
  { v: "last_week", label: "Last Week" },
  { v: "this_month", label: "This Month" },
  { v: "last_month", label: "Last Month" },
  { v: "last_30d", label: "Last 30 Days" },
  { v: "custom", label: "Custom Range…" },
];

// Format a JS Date to YYYY-MM-DD (local, no TZ shift)
const _todayISO = () => {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d - off).toISOString().slice(0, 10);
};

const Delta = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-[10px] font-mono text-zinc-400">—</span>;
  if (value === 0) return <span className="text-[10px] font-mono text-zinc-500 inline-flex items-center gap-0.5"><Minus size={10} /> 0%</span>;
  const up = value > 0;
  return (
    <span className={`text-[10px] font-mono inline-flex items-center gap-0.5 ${up ? "text-green-700" : "text-red-600"}`}>
      {up ? <TrendingUp size={10} /> : <TrendingDown size={10} />} {up ? "+" : ""}{value}%
    </span>
  );
};

const Metric = ({ icon: Icon, label, value, delta, testid }) => (
  <Card className="p-4 sm:p-5" data-testid={testid}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="data-label truncate">{label}</div>
        <div className="font-mono text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 mt-2">{value ?? 0}</div>
        <div className="mt-1"><Delta value={delta} /></div>
      </div>
      <Icon size={20} strokeWidth={1.5} className="text-zinc-400 shrink-0" />
    </div>
  </Card>
);

export default function Dashboard() {
  const { user, hasRole } = useAuth();
  const [period, setPeriod] = useState("last_30d");
  const [customStart, setCustomStart] = useState(() => {
    // Default custom start = 30 days ago
    const d = new Date(); d.setDate(d.getDate() - 29);
    const off = d.getTimezoneOffset() * 60000;
    return new Date(d - off).toISOString().slice(0, 10);
  });
  const [customEnd, setCustomEnd] = useState(_todayISO());
  const [data, setData] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetBackup, setResetBackup] = useState(null); // {id, filename, created_at, total_rows, size_bytes}
  const [snapBusy, setSnapBusy] = useState(false);

  const load = (p, cs, ce) => {
    if (p === "custom") {
      if (!cs || !ce) return;
      if (cs > ce) { toast.error("Start date must be on or before end date"); return; }
      return api.get(`/dashboard?period=custom&start=${cs}&end=${ce}`).then((r) => setData(r.data));
    }
    return api.get(`/dashboard?period=${p}`).then((r) => setData(r.data));
  };
  useEffect(() => {
    if (period !== "custom") load(period);
    else load("custom", customStart, customEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);
  // For custom range, only fetch when user clicks Apply (handled below)
  const applyCustom = () => load("custom", customStart, customEnd);

  const cur = data?.current || {};
  const d = data?.deltas || {};
  const a = data?.alerts || {};
  const trend = data?.trend || [];
  const funnel = data?.funnel || [];
  const stuck = data?.stuck || [];
  const range = data?.range || {};
  const rollStock = data?.roll_stock || { count: 0, weight_kg: 0 };
  const rej = data?.rejection || { total_cut: 0, total_rejected: 0, total_good: 0, rejection_pct: 0 };
  const stock = data?.stock || { available: 0, dispatched: 0, produced: 0 };
  const lead = data?.lead_time || { avg_days: null, sample_size: 0 };
  const pipeline = data?.pipeline || { total: 0, breakdown: [] };
  const ready = data?.ready_for_sewing || { qty: 0, batches: 0 };

  const doReset = async () => {
    if (confirmText !== "RESET") { toast.error('Type RESET to confirm'); return; }
    if (!resetBackup) { toast.error('Take a backup snapshot first'); return; }
    setResetting(true);
    try {
      await api.post("/admin/reset-data", { backup_id: resetBackup.id });
      toast.success("Production data cleared · pre-reset snapshot saved");
      setResetOpen(false); setConfirmText(""); setResetBackup(null);
      await load(period, customStart, customEnd);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setResetting(false); }
  };

  const takeResetSnapshot = async () => {
    setSnapBusy(true);
    try {
      const { data } = await api.post("/backup/snapshots", { label: "Pre-reset checkpoint" });
      setResetBackup(data);
      // Auto-download the ZIP so the admin keeps a local copy too
      const token = localStorage.getItem("token");
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/backup/snapshots/${data.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = data.filename; a.click();
        URL.revokeObjectURL(url);
      }
      toast.success(`Snapshot saved · ${data.total_rows.toLocaleString()} rows`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSnapBusy(false); }
  };

  return (
    <div data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 border-b border-zinc-200 bg-white px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
        <div>
          <div className="data-label">Live Production Overview</div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter text-zinc-950 mt-1">Control Room</h1>
          {range.from && (
            <div className="text-[11px] font-mono text-zinc-500 mt-1">
              {range.from} → {range.to} &nbsp;·&nbsp; vs {range.prev_from} → {range.prev_to}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={period} onChange={(e) => setPeriod(e.target.value)} className="!w-auto" data-testid="period-select">
            {PERIODS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
          </Select>
          {period === "custom" && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="custom-range-controls">
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                max={customEnd || undefined}
                className="!w-auto"
                data-testid="custom-start-input"
              />
              <span className="text-xs font-mono text-zinc-500">→</span>
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                min={customStart || undefined}
                max={_todayISO()}
                className="!w-auto"
                data-testid="custom-end-input"
              />
              <BtnSecondary onClick={applyCustom} data-testid="custom-apply-button">
                Apply
              </BtnSecondary>
            </div>
          )}
          {hasRole("admin") && (
            <BtnDanger onClick={() => setResetOpen(true)} data-testid="reset-data-button" className="flex items-center gap-1.5">
              <RotateCcw size={14} /> Reset Data
            </BtnDanger>
          )}
        </div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Top-row KPIs: Fabric Stock · Finished Stock · Rejection · Lead Time · Ready for Sewing */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          <Card className="p-4 sm:p-5" data-testid="metric-roll-stock">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="data-label">Uncut Fabric Stock</div>
                <div className="font-mono text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 mt-2">
                  {rollStock.weight_kg.toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-base text-zinc-500">kg</span>
                </div>
                <div className="text-[11px] font-mono text-zinc-500 mt-1">{rollStock.count} roll{rollStock.count === 1 ? "" : "s"}</div>
              </div>
              <Factory size={20} strokeWidth={1.5} className="text-zinc-400" />
            </div>
          </Card>

          <Card className="p-4 sm:p-5" data-testid="metric-finished-stock">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="data-label">Finished Stock</div>
                <div className="font-mono text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 mt-2">{stock.available.toLocaleString()} <span className="text-base text-zinc-500">pcs</span></div>
                <div className="text-[11px] font-mono text-zinc-500 mt-1">
                  produced {stock.produced.toLocaleString()} · shipped {stock.dispatched.toLocaleString()}
                </div>
              </div>
              <Package size={20} strokeWidth={1.5} className="text-zinc-400" />
            </div>
          </Card>

          <Card className="p-4 sm:p-5" data-testid="metric-rejection">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="data-label">Rejection Rate</div>
                <div className="font-mono text-2xl sm:text-3xl font-semibold tracking-tight text-red-600 mt-2">
                  {rej.rejection_pct}<span className="text-base">%</span>
                </div>
                <div className="text-[11px] font-mono text-zinc-500 mt-1">
                  {rej.total_rejected.toLocaleString()} rejected of {rej.total_cut.toLocaleString()} cut
                </div>
              </div>
              <XCircle size={20} strokeWidth={1.5} className="text-red-400" />
            </div>
          </Card>

          <Card className="p-4 sm:p-5" data-testid="metric-lead-time">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="data-label">Avg Production Cycle</div>
                <div className="font-mono text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 mt-2">
                  {lead.avg_days == null ? "—" : <>{lead.avg_days}<span className="text-base text-zinc-500"> days</span></>}
                </div>
                <div className="text-[11px] font-mono text-zinc-500 mt-1">
                  Fabric Receipt → Finished
                </div>
                {lead.sample_size > 0 && (
                  <div className="text-[10px] font-mono text-zinc-400 mt-1">
                    {lead.sample_size} completed batch{lead.sample_size === 1 ? "" : "es"} · median {lead.median_days}d · range {lead.min_days}–{lead.max_days}d
                  </div>
                )}
                {lead.sample_size === 0 && (
                  <div className="text-[10px] font-mono text-zinc-400 mt-1 italic">
                    No fully-completed batches yet
                  </div>
                )}
              </div>
              <Clock3 size={20} strokeWidth={1.5} className="text-zinc-400" />
            </div>
          </Card>

          <Card className="p-4 sm:p-5 border-green-200 bg-green-50/40" data-testid="metric-ready-for-sewing">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="data-label text-green-800">Ready for Sewing</div>
                <div className="font-mono text-2xl sm:text-3xl font-semibold tracking-tight text-green-700 mt-2">
                  {ready.qty.toLocaleString()} <span className="text-base text-green-700/70">pcs</span>
                </div>
                <div className="text-[11px] font-mono text-zinc-600 mt-1">
                  {ready.batches} batch{ready.batches === 1 ? "" : "es"} · rib + thread + printing OK
                </div>
              </div>
              <Shirt size={20} strokeWidth={1.5} className="text-green-600" />
            </div>
          </Card>
        </div>
        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          <Metric icon={Factory} label="Receipts" value={cur.receipts} delta={d.receipts} testid="metric-receipts" />
          <Metric icon={Factory} label="Rolls In" value={cur.rolls} delta={d.rolls} testid="metric-rolls" />
          <Metric icon={Scissors} label="Cut Pieces" value={cur.cut} delta={d.cut} testid="metric-cut" />
          <Metric icon={Printer} label="Sent for Printing" value={cur.print_sent} delta={d.print_sent} testid="metric-print-sent" />
          <Metric icon={Printer} label="Printed (Good)" value={cur.print_good} delta={d.print_good} testid="metric-print-good" />
          <Metric icon={Shirt} label="Sent for Sewing" value={cur.sew_sent} delta={d.sew_sent} testid="metric-sew-sent" />
          <Metric icon={Shirt} label="Sewn (Good)" value={cur.sew_good} delta={d.sew_good} testid="metric-sew-good" />
          <Metric icon={CheckCircle2} label="QC Passed" value={cur.qc_passed} delta={d.qc_passed} testid="metric-qc-passed" />
          <Metric icon={Flame} label="Ironed" value={cur.iron_done} delta={d.iron_done} testid="metric-iron" />
          <Metric icon={Truck} label="Dispatched" value={cur.dispatched} delta={d.dispatched} testid="metric-dispatch" />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2">
            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-200">
              <div className="data-label">Daily Output Trend (30 days)</div>
              <div className="font-heading text-lg sm:text-xl font-bold text-zinc-950 mt-1">Production velocity</div>
            </div>
            <div className="p-3 sm:p-4 h-64 sm:h-72">
              {data && trend.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <LineChart data={trend} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="#E4E4E7" vertical={false} />
                    <XAxis dataKey="date" stroke="#52525B" tickLine={false} axisLine={false} style={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis stroke="#52525B" tickLine={false} axisLine={false} style={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: "#FFF", border: "1px solid #E4E4E7", borderRadius: 2, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="cut" stroke="#09090B" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="printed" stroke="#2563EB" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="sewn" stroke="#F97316" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="dispatched" stroke="#16A34A" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : null}
            </div>
          </Card>

          <Card>
            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-200">
              <div className="data-label">Alerts</div>
              <div className="font-heading text-lg sm:text-xl font-bold text-zinc-950 mt-1">Attention needed</div>
            </div>
            <div className="p-4 sm:p-5 space-y-3">
              <AlertRow icon={AlertTriangle} label={`Rib needs attention${a.rib_missing ? ` (missing ${a.rib_missing})` : ""}`} value={a.rib_pending} tone={a.rib_pending > 0 ? "warning" : "success"} testid="alert-rib" />
              <AlertRow icon={AlertTriangle} label="Awaiting contrast rib (post-printing)" value={a.awaiting_contrast_rib || 0} tone={(a.awaiting_contrast_rib || 0) > 0 ? "warning" : "success"} testid="alert-contrast-rib" />
              <AlertRow icon={AlertTriangle} label={`Thread needs attention${a.thread_missing ? ` (missing ${a.thread_missing})` : ""}`} value={a.thread_pending} tone={a.thread_pending > 0 ? "warning" : "success"} testid="alert-thread" />
              <AlertRow icon={Clock} label="Stuck batches (&gt;7d)" value={a.stuck_count} tone={a.stuck_count > 0 ? "destructive" : "success"} testid="alert-stuck" />
            </div>
          </Card>
        </div>

        {/* In Pipeline · WIP between stages (all-time) */}
        <Card data-testid="pipeline-card">
          <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-200 flex items-baseline justify-between">
            <div>
              <div className="data-label">In Pipeline · WIP</div>
              <div className="font-heading text-lg sm:text-xl font-bold text-zinc-950 mt-1">{pipeline.total.toLocaleString()} pcs sitting between stages</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">Cash currently tied up on the floor — should trend down as orders flow through.</div>
            </div>
          </div>
          <div className="p-3 sm:p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3" data-testid="pipeline-breakdown">
            {pipeline.breakdown.map((p) => (
              <div key={p.stage} className={`border ${p.qty > 0 ? "border-zinc-200" : "border-zinc-100 bg-zinc-50"} rounded-sm p-3`} data-testid={`pipeline-stage-${p.stage.split(" ")[0].toLowerCase()}`}>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 leading-tight">{p.stage}</div>
                <div className={`font-mono font-bold mt-1.5 ${p.qty > 0 ? "text-zinc-950 text-xl" : "text-zinc-300 text-lg"}`}>{p.qty.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Funnel */}
        <Card>
          <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-200">
            <div className="data-label">All-time Production Funnel</div>
            <div className="font-heading text-lg sm:text-xl font-bold text-zinc-950 mt-1">Pieces across every stage</div>
          </div>
          <div className="p-3 sm:p-4 h-64 sm:h-72">
            {funnel.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                <BarChart data={funnel} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="stage" stroke="#52525B" tickLine={false} axisLine={false} style={{ fontSize: 11 }} />
                  <YAxis stroke="#52525B" tickLine={false} axisLine={false} style={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#FFF", border: "1px solid #E4E4E7", borderRadius: 2, fontSize: 12 }} />
                  <Bar dataKey="qty" fill="#09090B" radius={0} />
                </BarChart>
              </ResponsiveContainer>
            ) : null}
          </div>
        </Card>

        {/* Stuck batches */}
        {stuck.length > 0 && (
          <Card>
            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-200">
              <div className="data-label">Batches Stuck &gt; 7 days</div>
              <div className="font-heading text-lg sm:text-xl font-bold text-zinc-950 mt-1">Needs unblocking</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[540px]" data-testid="stuck-batches-table">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-zinc-500 uppercase tracking-widest">Batch</th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-zinc-500 uppercase tracking-widest">Roll</th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-zinc-500 uppercase tracking-widest">Style</th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-zinc-500 uppercase tracking-widest">Stage</th>
                    <th className="text-right py-2.5 px-4 text-[11px] font-semibold text-zinc-500 uppercase tracking-widest">Days</th>
                  </tr>
                </thead>
                <tbody>
                  {stuck.map((b) => (
                    <tr key={b.id} className="border-b border-zinc-100">
                      <td className="py-2.5 px-4 text-sm font-mono font-semibold">{b.batch_code}</td>
                      <td className="py-2.5 px-4 text-sm font-mono"><RollLink rollNo={b.roll_no} /></td>
                      <td className="py-2.5 px-4 text-sm">{b.style_name}</td>
                      <td className="py-2.5 px-4"><span className="stage-badge status-warning">{b.stage}</span></td>
                      <td className="py-2.5 px-4 text-sm font-mono font-semibold text-right text-red-600">{b.days_stuck} d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Reset Data Modal */}
      <Modal open={resetOpen} onClose={() => { setResetOpen(false); setConfirmText(""); setResetBackup(null); }} title="Reset Production Data" testid="reset-modal">
        <div className="space-y-4 text-sm">
          <div className="border border-red-200 bg-red-50 text-red-800 p-3 rounded-sm">
            <div className="font-semibold mb-1">This cannot be undone.</div>
            <div className="text-xs">
              All fabric receipts, rolls, cutting batches, printing/sewing dispatches & receipts, QC, ironing, finished stock, dispatches, and audit logs will be <span className="font-semibold">permanently deleted</span>.
              <br />
              Roll numbers and batch codes will restart from 1.
            </div>
          </div>
          <div className="border border-zinc-200 bg-zinc-50 text-zinc-700 p-3 rounded-sm text-xs">
            <div className="font-semibold mb-1">Preserved:</div>
            Users · Vendors · Threads · Ribs · Fabric Types · Styles
          </div>

          {/* Step 1 — mandatory snapshot */}
          <div className={`border p-3 rounded-sm text-xs ${resetBackup ? "border-green-200 bg-green-50 text-green-900" : "border-orange-300 bg-orange-50 text-orange-900"}`}>
            <div className="font-semibold mb-1.5 flex items-center justify-between">
              <span>Step 1 · Mandatory backup</span>
              {!resetBackup && (
                <BtnSecondary onClick={takeResetSnapshot} disabled={snapBusy} data-testid="reset-take-snapshot-button">
                  {snapBusy ? "Snapping…" : "Take Snapshot & Download"}
                </BtnSecondary>
              )}
            </div>
            {resetBackup ? (
              <div data-testid="reset-snapshot-ok">
                ✓ Snapshot saved · <span className="font-mono">{resetBackup.filename}</span> · {resetBackup.total_rows?.toLocaleString?.() || 0} rows · downloaded to your device. You may proceed.
              </div>
            ) : (
              <div>A full server-side snapshot + local ZIP download is required before Reset is allowed. Click to take one now — takes just a moment.</div>
            )}
          </div>

          {/* Step 2 — confirmation text */}
          <div>
            <label className="data-label block mb-1.5">Step 2 · Type <span className="font-mono text-red-600">RESET</span> to confirm</label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="reset-confirm-input" placeholder="RESET" disabled={!resetBackup} />
          </div>
          <div className="flex justify-end gap-2">
            <BtnSecondary onClick={() => { setResetOpen(false); setConfirmText(""); setResetBackup(null); }}>Cancel</BtnSecondary>
            <BtnDanger onClick={doReset} disabled={resetting || confirmText !== "RESET" || !resetBackup} data-testid="reset-confirm-button">
              {resetting ? "Resetting…" : "Reset All Production Data"}
            </BtnDanger>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const AlertRow = ({ icon: Icon, label, value, tone, testid }) => {
  const colors = {
    warning: "text-orange-600 bg-orange-50 border-orange-200",
    success: "text-green-700 bg-green-50 border-green-200",
    destructive: "text-red-700 bg-red-50 border-red-200",
  };
  return (
    <div className={`flex items-center justify-between border ${colors[tone]} px-3 py-2.5 rounded-sm`} data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={16} />
        <span className="text-sm font-medium" dangerouslySetInnerHTML={{ __html: label }} />
      </div>
      <span className="font-mono text-lg font-semibold">{value ?? 0}</span>
    </div>
  );
};
